import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { formatDistanceToNow } from "date-fns";
import React from "react";
import { toast } from "react-toastify";
import { getPrimaryCategory } from "../../../constants";
import { useDashboardAccessRequests } from "../../../hooks/react-query/access-requests/useDashboardAccessRequests";
import {
  useApproveDashboardAccessRequest,
  useRejectDashboardAccessRequest,
} from "../../../hooks/react-query/access-requests/useDecideDashboardAccessRequest";
import { useDashboardRegistry } from "../../../hooks/react-query/dashboards/useDashboardRegistry";
import { ProfilePicture } from "../../UserProfile/common/ProfilePicture";
import { DashboardAccessRequest } from "./dashboardAccessRequestsMockData";
import "../../../assets/styles/pages/adminRequests.scss";

// Falls back to email when neither name field is set - matches ProfilePicture's own
// firstname={... || request.user.email || " "} fallback used right below.
const getRequestDisplayName = (request: DashboardAccessRequest): string =>
  request.user.first_name || request.user.last_name ? `${request.user.first_name} ${request.user.last_name}`.trim() : request.user.email;

export const AdminRequests = () => {
  const { requests } = useDashboardAccessRequests();
  const { dashboards } = useDashboardRegistry();
  const approveMutation = useApproveDashboardAccessRequest();
  const rejectMutation = useRejectDashboardAccessRequest();

  // Plain approve/reject - no access-level selection on this card anymore. Approving still grants
  // this specific dashboard automatically, server-side (grant_view_access), independent of anything
  // here.
  const handleApprove = (request: DashboardAccessRequest) => {
    const displayName = getRequestDisplayName(request);

    approveMutation.mutate(
      { id: request.id },
      {
        onSuccess: (result) => {
          if (result.status === "success") {
            toast.success(`Access granted to ${displayName}`);
          } else {
            toast.error(`Couldn't approve the request from ${displayName} - please try again.`);
          }
        },
      }
    );
  };

  const handleReject = (request: DashboardAccessRequest) => {
    rejectMutation.mutate(
      { id: request.id },
      {
        onSuccess: (result) => {
          if (result.status === "success") {
            toast.success(`Request from ${getRequestDisplayName(request)} rejected`);
          } else {
            toast.error(`Couldn't reject the request from ${getRequestDisplayName(request)} - please try again.`);
          }
        },
      }
    );
  };

  return (
    <div className="admin_tile admin_requests">
      <div className="admin_requests_header">
        <div className="admin_requests_header_left">
          <PersonAddIcon />
          <h3>DASHBOARD ACCESS REQUESTS</h3>
        </div>
        <span className="admin_requests_pending">{requests.length} pending</span>
      </div>
      <div className="admin_requests_divider" />

      <div className="admin_requests_list">
        {requests.length === 0 ? (
          <p className="admin_requests_empty">No pending requests.</p>
        ) : (
          requests.map((request) => {
            // The request only carries {id, name} for its dashboard - cross-reference the full
            // registry entry to get its category for the "Requesting (...)" line below.
            const matchedDashboard = dashboards.find((dashboard) => dashboard.id === request.dashboard.id);
            const category = matchedDashboard ? getPrimaryCategory(matchedDashboard) : "Uncategorized";
            const displayName = getRequestDisplayName(request);

            return (
              <div className="admin_request_item" key={request.id} data-test-id={`admin-request-${request.id}`}>
                <ProfilePicture firstname={request.user.first_name || request.user.email || " "} lastname={request.user.last_name || " "} />
                <div className="admin_request_item_details">
                  <p className="admin_request_item_name">
                    {displayName}
                    {displayName !== request.user.email && <span className="admin_request_item_email">{request.user.email}</span>}
                  </p>
                  <p className="admin_request_item_view">{request.internal_view}</p>
                  <p className="admin_request_item_meta">
                    <strong>
                      Requesting ({category}: {request.dashboard.name})
                    </strong>
                    <span className="admin_request_item_dot">•</span>
                    {formatDistanceToNow(new Date(request.requested_at), { addSuffix: true })}
                  </p>
                </div>
                <div className="admin_request_item_actions">
                  <button
                    className="admin_request_item_accept"
                    onClick={() => handleApprove(request)}
                    data-test-id={`admin-request-approve-${request.id}`}
                  >
                    <CheckIcon fontSize="small" />
                    Approve
                  </button>
                  <button
                    className="admin_request_item_reject"
                    onClick={() => handleReject(request)}
                    data-test-id={`admin-request-reject-${request.id}`}
                  >
                    <CloseIcon fontSize="small" />
                    Reject
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
